<template>
  <view-page
    :title="title"
    :loading="loading"
    :delete-dialog-title="$t('DeleteCustomField')"
    :delete-dialog-content="deleteMessage"
    :delete-button="userHasPermissionTo('destroy')"
    :edit-button="userHasPermissionTo('update')"
    @editItem="editItem"
    @deleteItem="deleteField"
    :permission-name="permissionName"
  >
    <template v-slot:leftSide>
      <view-page-data-field
        v-for="field in fields"
        :key="field.template"
        :icon="field.icon"
        :label="field.label"
        :data="field.data"
      />
      <app-dialog-box
      :open="confirmDeleteDialog"
      :title="$t('DeleteCustomField')"
      :content="$t('CustomFieldInUse')"
      :main-action-label="$t('No')"
      @mainAction="confirmDeleteDialog = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="finalDelete"
      :loading="loadingDelete"
    />
    </template>
  </view-page>
</template>

<script>
import { mapActions } from "vuex";
import ViewPage from "@/components/Management/ViewPage/ViewPage";
import ViewPageDataField from "@/components/Management/ViewPage/ViewPageDataField";
import ManagementButton from "@/components/Management/ManagementButton";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { customFieldsMixin } from "@/mixins/customFieldsMixin";
import { materialFamilyMixin } from "@/mixins/management/materialFamilyMixin";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
export default {
  name: "CustomFieldsDetails",

  components: {
    ViewPageDataField,
    ViewPage,
    ManagementButton,
    AppDialogBox
  },

  mixins: [
    managementDatatableMixin,
    materialFamilyMixin,
    checkPermissionsMixin,
    customFieldsMixin
  ],

  data() {
    return {
      customField: null,
      loading: false,
      permissionName: "custom_field",
      fields: null,
      loadingDelete: false,
      confirmDeleteDialog: false,
    };
  },

  computed: {
    customFieldUuid() {
      return this.$route.params.uuid;
    },

    title() {
      return this.customField
        ? this.customField.label
        : this.$i18n.tc("CustomFields", 1);
    },

    deleteMessage() {
      return this.customField
        ? this.$i18n.t("DeleteConfirmation") +
            " " +
            this.customField.label +
            "?"
        : "";
    }
  },

  watch: {
    customFieldUuid() {
      this.getCustomField();
    },

    list() {
      this.getSelectDefaultValue();
    },

    forms() {
      this.fields = this.getFields();
    },

    objectsAndMaterialFamilies() {
      this.fields = this.getFields();
    }
  },

  mounted() {
    this.getCustomField();
  },

  methods: {
    ...mapActions(["fetchCustomFieldByUuid", "deleteCustomFieldByUuid"]),

    async getCustomField() {
      if (this.customFieldUuid) {
        this.loading = true;
        try {
          this.customField = await this.fetchCustomFieldByUuid(
            this.customFieldUuid
          );
          this.getCustomLists(this.customField);
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
          this.fields = this.getFields();
        }
      }
    },

    async deleteField() {
      try {
        this.loadingDelete = true;
        await this.deleteCustomFieldByUuid({id: this.customField.id, ignoreEntry: 'false'});
        this.$router.back();
      } catch (error) {
        if (error.response.status === 403) {
          this.confirmDeleteDialog = true;
          return;
        }
        console.error(error);
        this.$toasted.global.toast_error(error.response.data.data);
      } finally {
        this.loadingDelete = false;
      }
    },

    async finalDelete(){
      this.confirmDeleteDialog = false;
      await this.deleteCustomFieldByUuid({id: this.customField.id, ignoreEntry: 'true'});
      this.$router.back();
    },

    editItem() {
      this.$router.push({
        name: "custom-field-edit",
        params: { uuid: this.customFieldUuid }
      });
    },

    getFields() {
      if (this.customField) {
        return [
          {
            icon: "code",
            label: this.$i18n.tc("Code", 1),
            data: this.customField.code
          },
          {
            icon: "label",
            label: this.$i18n.tc("Label", 1),
            data: this.customField.label
          },
          {
            icon: "label",
            label: this.$i18n.tc("Object", 1),
            data: this.getObjectData()
          },
          {
            icon: "layers",
            label: this.$i18n.tc("Type", 1),
            data: this.getInputTypeName(this.customField.input_type)
          },
          {
            icon: "list",
            label: this.$i18n.t("List"),
            data: this.customField.list_name
          },
          {
            icon: "settings_backup_restore",
            label: this.$i18n.t("DefaultValue"),
            data: this.customField.default_value
          },
          {
            icon: "emergency",
            label: this.$i18n.t("Required"),
            data: this.customField.required
              ? this.$i18n.t("True")
              : this.$i18n.t("False")
          },
          {
            icon: "straighten",
            label: this.$i18n.tc("Size", 1),
            data: this.getFieldSize(this.customField.size)
          },
          {
            icon: "toggle_off",
            label: this.$i18n.t("Disabled"),
            data: this.customField.disable
              ? this.$i18n.t("True")
              : this.$i18n.t("False")
          },
          {
            icon: "format_list_numbered",
            label: this.$i18n.t("Index"),
            data: this.customField.index
          }
        ];
      }
      return null;
    },

    getSelectDefaultValue() {
      if (
        this.customField.input_type === "select" ||
        this.customField.input_type === "multiselect"
      ) {
        let default_string = "";
        for (let i = 0; i < this.customField.default_value.length; i++) {
          const element = this.customField.default_value[i];
          let field = this.list.find(item => item.id === element);
          if (field) {
            i === 0
              ? (default_string += field.label)
              : (default_string += ", " + field.label);
          }
        }
        this.customField.default_value = default_string;
      }
      this.fields = this.getFields();
    },

    getObjectData() {
      let objectTypeName = this.getObjectTypeName(
        this.customField.object_type,
        this.customField.table
      );
      let family = this.customField.object_type
        ? " (" + this.$i18n.tc("Family", 2) + ")"
        : " (" + this.$i18n.tc("Form", 2) + ")";
      return objectTypeName + family;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SystemManagement/SystemSettings/CustomFields/CustomFieldsDetails.vue